import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Hub } from "../../common/models";
import { Skeleton, Box, Paper, TextField, Checkbox, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import {
  DataGrid,
  GridCheckIcon,
  GridCloseIcon,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { HubSearch } from "../models";
import hubsService from "services/hubsService";
import MDButton from "components/MDButton";

const HubsList = () => {
  let persistedSearch = localStorage.getItem("hubs_list");
  let initialSearch: HubSearch = {
    pagedSearchOptions: { offset: 0, pageSize: 25 },
    sortAsc: true,
    sortName: "name",
    isActive: "active",
  };
  const [search, setSearch] = useState<HubSearch>(persistedSearch ? JSON.parse(persistedSearch) : initialSearch);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useGridApiRef();

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any, number>) => <Link to={`${params.row.id}`}>{params.value}</Link>,
    },
    { field: "rcxLocId", headerName: "Location ID", flex: 1 },
    {
      field: "isActive",
      headerName: "Active",
      width: 90,
      align: "center",
      renderCell: (params: GridRenderCellParams<any, boolean>) => params.value ? <GridCheckIcon style={{fill: "green"}}/>: <GridCloseIcon style={{fill: "red", textAlign:"center"}} />,
    },
  ];

  useEffect(() => {
    setIsLoading(true);
    localStorage.setItem("hubs_list", JSON.stringify(search));
    hubsService
      .searchHubs(search)
      .then((data) => {
        setHubs(data.results);
        setTotal(data.count);
      })
      .finally(() => setIsLoading(false));
  }, [search]);

  function handleOnChange(e: any) {
    var request = { ...search, [e.target.name]: e.target.value };
    setSearch(request);
    gridRef.current.setPage(0);
  }

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    var request = {
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort == "asc",
      pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
    };
    setSearch(request);
    gridRef.current.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize != search.pagedSearchOptions.pageSize) {
      pagination.page = 0;
    }
    setSearch({ ...search, pagedSearchOptions: { pageSize: pagination.pageSize, offset: pagination.page } });
  };

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(search.pagedSearchOptions.pageSize)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );
  const clearFilters = ()=>{
    setSearch({...initialSearch, rcxLocId: undefined, name: ''});
  }
  return (
    <>
      <Box>
        <TextField
          sx={{ mt: 2, ml: 1 }}
          type="number"
          name="rcxLocId"
          label="Location ID"
          value={search.rcxLocId || ''}
          onChange={handleOnChange}
        />
        <TextField sx={{ mt: 2, ml: 1 }} name="name" label="Name" value={search.name} onChange={handleOnChange} />
        <FormControl sx={{ mt: 2, ml: 1, p: 0, minWidth: 120 }}>
          <InputLabel id="isActive-label">Status</InputLabel>
          <Select
            SelectDisplayProps={{ style: { padding: "0.75rem !important" as any } }}
            id="isActive"
            name="isActive"
            labelId="isActive-label"
            label="Status"
            value={search.isActive}
            onChange={handleOnChange}
            multiple={false}
            sx={{ p: 0 }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">InActive</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
        <MDButton sx={{mt:2, ml:1}} onClick={clearFilters}>Clear</MDButton>
      </Box>
      <Box sx={{ mt: 2, overflow: "hidden" }}>
        <Paper sx={{ width: "99%", height: isLoading ? "100vh" : "auto", overflow: "hidden" }}>
          <DataGrid
            sx={{ overflow: "hidden", fontSize: ".95em" }}
            rows={hubs}
            columns={columns}
            pagination
            sortingMode="server"
            paginationMode="server"
            onPaginationModelChange={handlePaginationModel}
            onSortModelChange={handleSortModelChange}
            initialState={{
              pagination: { paginationModel: search.pagedSearchOptions },
              sorting: { sortModel: [{ field: "name", sort: "asc" }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={total}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
          />
        </Paper>
      </Box>
    </>
  );
};
export default HubsList;
